<template>
  <div class="flex flex-col w-full gap-6 pt-2">
    <div class="flex flex-col gap-1">
      <div class="text-2xl font-semibold primary_color">Audit Trail</div>
      <div class="text-sm secondary_color">
        View all the changes made in your workspace
      </div>
    </div>

    <div class="flex flex-col w-full gap-2 proxima_nova">
      <SkeletonLoader
        v-if="isLoading && !audits?.length"
        :page="loaderPages.AUDIT_TRAIL"
      />

      <div
        v-else-if="filteredAudits?.length > 0"
        class="flex flex-col gap-2 w-full overflow-y-scroll"
        style="height: 75vh"
        @scroll="handleScroll"
        ref="scrollContainer"
      >
        <AuditTrailRenderer
          source="settings"
          :audits="filteredAudits"
          :is-last-page="!paginationData.hasMore"
        />
      </div>

      <EmptyScreen
        v-else-if="!isLoading"
        source="settings_audit_trails"
        text="No audit logs found!"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, nextTick, computed } from "vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import DashboardService from "@/src/api/DashboardService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import AuditTrailRenderer from "@/src/components/audit_components/AuditTrailRenderer.vue";

const audits = ref([]);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const scrollContainer = ref(null);

const paginationData = ref({
  currentPage: 1,
  pageSize: 140,
  hasMore: true,
  total: 0,
});

const filteredAudits = computed(() => {
  // For now, show all audit logs in workspace settings
  // Future enhancement: add filter options for action type
  return audits.value;
});

const resetPagination = () => {
  paginationData.value = {
    currentPage: 1,
    pageSize: 140,
    hasMore: true,
    total: 0,
  };
  audits.value = [];
};

const handleScroll = () => {
  try {
    const container = scrollContainer.value;
    if (!container) return;

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Load more when scrolling down and near the bottom
    if (
      scrollTop + clientHeight >= scrollHeight - 150 &&
      paginationData.value.hasMore &&
      !isLoadingMore.value
    ) {
      toFetchAuditLogs(paginationData.value.currentPage + 1, true);
    }
  } catch (error) {
    console.error("An error occurred while handling scroll: ", error);
  }
};

const enrichAuditLog = (audit) => {
  try {
    // If entity_details already exists at top level, return as is
    if (audit.entity_details) {
      return audit;
    }

    // Handle different entity types
    switch (audit.entity_type) {
      case "SequenceExecution":
        return enrichSequenceExecution(audit);
      case "Sequence":
        return enrichSequence(audit);
      case "SequenceTag":
        return enrichSequenceTag(audit);
      case "SequenceStep":
        return enrichSequenceStep(audit);
      case "SequenceSetting":
        return enrichSequenceSetting(audit);
      default:
        // For other entity types, return as is
        // The AuditTrailRenderer will handle them based on before/after
        return audit;
    }
  } catch (error) {
    console.error("Error enriching audit log:", error);
    return audit;
  }
};

const enrichSequenceExecution = (audit) => {
  if (!audit.after) return audit;

  const entityData = audit.after.entity_details;
  if (!entityData) return audit;

  const entity = extractEntityFromAttributes(entityData);
  return {
    ...audit,
    entity_details: {
      created_at: audit.after.created_at,
      entity_type: entityData.entity_type,
      entity_id: entityData.entity_id,
      entity: entity,
      id: audit.after.id,
      reply_intent: audit.after.reply_intent || null,
      sequence_id: audit.after.sequence_id,
      status: audit.after.status,
      updated_at: audit.after.updated_at,
    },
  };
};

const enrichSequence = (audit) => {
  if (!audit.after && !audit.before) return audit;

  const sequenceData = audit.after || audit.before;
  return {
    ...audit,
    entity_details: {
      id: sequenceData.id,
      name: sequenceData.name,
      status: sequenceData.status,
      created_at: sequenceData.created_at,
      updated_at: sequenceData.updated_at,
      owner_id: sequenceData.owner_id,
      workspace_id: sequenceData.workspace_id,
    },
  };
};

const enrichSequenceTag = (audit) => {
  if (!audit.after && !audit.before) return audit;

  const tagData = audit.after || audit.before;
  return {
    ...audit,
    entity_details: {
      id: tagData.id,
      tag: tagData.tag,
      sequence_id: tagData.sequence_id,
      created_at: tagData.created_at,
      updated_at: tagData.updated_at,
      workspace_id: tagData.workspace_id,
    },
  };
};

const enrichSequenceStep = (audit) => {
  if (!audit.after && !audit.before) return audit;

  const stepData = audit.after || audit.before;
  return {
    ...audit,
    entity_details: {
      id: stepData.id,
      position: stepData.position,
      step_type: stepData.step_type,
      sequence_id: stepData.sequence_id,
      created_at: stepData.created_at,
      updated_at: stepData.updated_at,
    },
  };
};

const enrichSequenceSetting = (audit) => {
  if (!audit.after && !audit.before) return audit;

  const settingData = audit.after || audit.before;
  return {
    ...audit,
    entity_details: {
      id: settingData.id,
      sequence_id: settingData.sequence_id,
      created_at: settingData.created_at,
      updated_at: settingData.updated_at,
    },
  };
};

const extractEntityFromAttributes = (entityData) => {
  try {
    if (!entityData || !entityData.attributes) return null;

    const attrs = entityData.attributes;
    return {
      id: entityData.entity_id,
      first_name: attrs.name?.first_name || "",
      last_name: attrs.name?.last_name || "",
      email: attrs.work_emails?.[0]?.email || attrs.personal_emails?.[0]?.email || null,
      current_company: attrs.current_company?.value || null,
      current_role: attrs.current_role?.value || null,
      description: attrs.description?.value || null,
      linkedin_url: attrs.linkedin_url?.value || null,
      phone_number: attrs.phone_numbers?.[0]?.number || "",
      lead_status: attrs.lead_status?.value || null,
      created_at: attrs.created_at?.value || null,
      updated_at: attrs.updated_at?.value || null,
      workspace_id: entityData.workspace_id,
      owner_id: attrs.owner?.value || null,
      profile_pic: attrs.profile_pic?.value || null,
    };
  } catch (error) {
    console.error("Error extracting entity from attributes:", error);
    return null;
  }
};

const toFetchAuditLogs = async (page = 1, isLoadMore = false) => {
  try {
    if (!isLoadMore) {
      isLoading.value = true;
    } else {
      isLoadingMore.value = true;
    }

    const params = {
      page: page,
      size: paginationData.value.pageSize,
    };

    const response = await DashboardService.fetchWorkspaceAuditLogs(params);

    if (response?.success) {
      const newAudits = (response?.audit_logs || []).map(enrichAuditLog);

      if (isLoadMore) {
        // Append new data at the end for scroll-down pagination
        audits.value = [...audits.value, ...newAudits];
      } else {
        audits.value = newAudits;
      }

      paginationData.value = {
        currentPage: response.page || page,
        pageSize: response.size || paginationData.value.pageSize,
        hasMore: response.hasMore || false,
        total: response.total || 0,
      };
    }
  } catch (error) {
    console.error(
      "An error occurred while trying to fetch workspace audit logs: ",
      error,
    );
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
  }
};

onMounted(() => {
  resetPagination();
  toFetchAuditLogs();
});
</script>

